import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Button,
  Space,
  Typography,
  Alert,
  Tooltip,
  Skeleton,
  Row,
  Col,
  Tag,
  Table,
} from 'antd';
import {
  ArrowLeftOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  WarningFilled,
  RobotOutlined,
  BranchesOutlined,
  SafetyOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { useStore } from '@/stores';
import type { ScenarioResultSummary, RunSummary } from '@/api/validationRuns';
import { colors } from '@/theme/tokens';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { StatusPill, type PillTone } from '@/components/StatusPill';
import { KpiTile } from '@/components/KpiTile';
import { BrandedEmpty } from '@/components/BrandedEmpty';
import { HelpHotspot } from '@/features/help/HelpHotspot';
import { FindingDrawer } from './FindingDrawer';
import { CompareRunsDrawer } from './CompareRunsDrawer';
import { RunReadingPanel } from './RunReadingPanel';
import { useRunLinks } from '@/features/scenario-intelligence/workspace/paths';

const { Text, Paragraph } = Typography;

const STATUS_TONE: Record<string, PillTone> = {
  Completed: 'green',
  Running: 'ink',
  Aborted: 'amber',
  Errored: 'red',
};

const ENV_TONE: Record<string, PillTone> = {
  PROD: 'red',
  Prod: 'red',
  QA: 'amber',
  qa: 'amber',
  local: 'slate',
  Local: 'slate',
};

// Dedicated page for one ValidationRun. Hero with run-level KPIs, then a
// scenarios table where each row shows pass/fail/safety/critical and links
// to the per-scenario detail (turns, router decisions, assertions) when we
// add that surface in a follow-up.

export const RunDetailPage = observer(function RunDetailPage() {
  const params = useParams<{ id: string }>();
  const id = Number(params.id);
  const navigate = useNavigate();
  const links = useRunLinks();
  const { validationRunsStore: store, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const [openResultId, setOpenResultId] = useState<number | null>(null);
  const [compareOpen, setCompareOpen] = useState(false);

  useEffect(() => {
    if (Number.isFinite(id)) {
      void store.loadRun(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!Number.isFinite(id)) {
    return (
      <SectionCard>
        <BrandedEmpty
          icon={<PlayCircleOutlined />}
          title="Invalid run id"
          description="The id in the URL is not a number."
          size="lg"
        />
      </SectionCard>
    );
  }

  if (store.isLoadingRun && !store.openRun) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <BackLink onClick={() => navigate(links.runs)} isDark={isDark} />
        <SectionCard>
          <Skeleton active paragraph={{ rows: 6 }} />
        </SectionCard>
      </div>
    );
  }

  if (store.runError) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <BackLink onClick={() => navigate(links.runs)} isDark={isDark} />
        <Alert type="error" showIcon message="Failed to load run" description={store.runError} />
      </div>
    );
  }

  if (!store.openRun) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <BackLink onClick={() => navigate(links.runs)} isDark={isDark} />
        <SectionCard>
          <BrandedEmpty
            icon={<PlayCircleOutlined />}
            title="Run not found"
            description="No run with this id is visible in your active merchant."
          />
        </SectionCard>
      </div>
    );
  }

  const run = store.openRun.run;
  const results = store.openRun.results;
  const reading = store.openRun.reading;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <BackLink onClick={() => navigate(links.runs)} isDark={isDark} />

      <PageHeader
        accent="green"
        icon={<PlayCircleOutlined />}
        title={
          <Space size={6} align="center">
            <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>Run #{run.id}</span>
            <HelpHotspot topic="validations.runs.detail" />
          </Space>
        }
        subtitle={
          <Space size={6} wrap>
            <Link
              to={`/agents/${run.aiAgentId}`}
              style={{ color: colors.ink[600], fontWeight: 600 }}
            >
              <RobotOutlined /> {run.agentName ?? `Agent #${run.aiAgentId}`}
            </Link>
            {run.suiteSlug && (
              <span
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 12,
                  color: isDark ? colors.slate[400] : colors.slate[500],
                }}
              >
                · {run.suiteSlug}
              </span>
            )}
          </Space>
        }
        actions={
          <Space wrap>
            <Button icon={<SwapOutlined />} onClick={() => setCompareOpen(true)}>
              Compare with another run
            </Button>
            <Button icon={<ReloadOutlined />} onClick={() => void store.loadRun(id)}>
              Refresh
            </Button>
          </Space>
        }
      />

      {/* SI-03b: what was tested, then what needs a person. A run read before the reading existed keeps its KPI row. */}
      {reading ? (
        <RunReadingPanel reading={reading} results={results} onOpen={setOpenResultId} />
      ) : (
        <>
          {store.openRun.readingProblem && <Alert type="warning" showIcon title={store.openRun.readingProblem} />}
          <RunKpiRow run={run} />
        </>
      )}

      {/* Run metadata strip */}
      <RunMetadata run={run} isDark={isDark} />

      {/* Scenarios table */}
      <SectionCard
        title={
          <Space size={6}>
            <span>{reading ? 'All results as recorded' : 'Scenarios'}</span>
            <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
              {results.length} {results.length === 1 ? 'result' : 'results'}
            </Text>
          </Space>
        }
        subtitle="Each scenario's pass/fail outcome. Click a scenario to open its conversation: what was expected, what the agent said and did, and what the record cannot show."
        noPadding
      >
        <ScenariosTable results={results} isDark={isDark} onOpen={setOpenResultId} />
      </SectionCard>

      {/* Compare before Finding: a result opened from the comparison stacks on top of it. */}
      <CompareRunsDrawer run={run} open={compareOpen} onClose={() => setCompareOpen(false)} onOpenResult={setOpenResultId} />
      <FindingDrawer resultId={openResultId} onClose={() => setOpenResultId(null)} />
    </div>
  );
});

// ── Back-link ──────────────────────────────────────────────────────────────

function BackLink({ onClick, isDark }: { onClick: () => void; isDark: boolean }) {
  return (
    <div>
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={onClick}
        style={{ paddingLeft: 0, color: isDark ? colors.slate[300] : colors.slate[500] }}
      >
        All runs
      </Button>
    </div>
  );
}

// ── KPI row ────────────────────────────────────────────────────────────────

const RunKpiRow = observer(function RunKpiRow({ run }: { run: RunSummary }) {
  const passRate = Number(run.overallPassRate);
  const passAccent: 'red' | 'amber' | 'green' =
    run.deploymentBlocked ? 'red' : passRate >= 95 ? 'green' : passRate >= 80 ? 'amber' : 'red';

  return (
    <Row gutter={[12, 12]}>
      <Col xs={24} sm={12} md={6}>
        <KpiTile
          accent={passAccent}
          label="Pass rate"
          value={`${passRate.toFixed(0)}%`}
          hint={`${run.passedScenarios}/${run.totalScenarios} scenarios passed`}
          icon={
            run.failedScenarios === 0 ? (
              <CheckCircleFilled />
            ) : run.deploymentBlocked ? (
              <WarningFilled />
            ) : (
              <CloseCircleFilled />
            )
          }
        />
      </Col>
      <Col xs={24} sm={12} md={6}>
        <KpiTile
          accent="ink"
          label="Avg score"
          value={`${Number(run.averageScore).toFixed(0)}`}
          hint="0–100, average across scenarios"
          icon={<SafetyOutlined />}
        />
      </Col>
      <Col xs={24} sm={12} md={6}>
        <KpiTile
          accent="violet"
          label="Failed scenarios"
          value={run.failedScenarios}
          hint={
            run.status === 'Aborted'
              ? 'Run stopped before every scenario ran; it cannot count as a pass'
              : run.deploymentBlocked
              ? 'A scenario failed, so this run cannot support a release'
              : run.failedScenarios === 0
              ? 'Clean run'
              : 'Investigate the failing scenarios below'
          }
          icon={<CloseCircleFilled />}
        />
      </Col>
      <Col xs={24} sm={12} md={6}>
        <KpiTile
          accent="amber"
          label="Total elapsed"
          value={formatElapsed(run.totalElapsedMs)}
          hint={
            run.completedDate
              ? `Completed ${new Date(run.completedDate).toLocaleString()}`
              : 'Still running'
          }
          icon={<ClockCircleOutlined />}
        />
      </Col>
    </Row>
  );
});

// ── Metadata strip ─────────────────────────────────────────────────────────

function RunMetadata({ run, isDark }: { run: RunSummary; isDark: boolean }) {
  return (
    <SectionCard>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 16,
        }}
      >
        <MetaCell label="Status" isDark={isDark}>
          {run.status ? (
            <StatusPill tone={STATUS_TONE[run.status] ?? 'slate'}>{run.status}</StatusPill>
          ) : (
            <Text type="secondary">—</Text>
          )}
        </MetaCell>
        <MetaCell label="Run type" isDark={isDark}>
          {run.runType ? (
            <StatusPill tone={run.runType === 'MultiTurn' ? 'violet' : 'slate'}>
              {run.runType}
            </StatusPill>
          ) : (
            <Text type="secondary">—</Text>
          )}
        </MetaCell>
        <MetaCell label="Environment" isDark={isDark}>
          {run.environment ? (
            <StatusPill tone={ENV_TONE[run.environment] ?? 'slate'}>{run.environment}</StatusPill>
          ) : (
            <Text type="secondary">—</Text>
          )}
        </MetaCell>
        <MetaCell label="Trigger" isDark={isDark}>
          {run.triggerSource ? (
            <StatusPill tone="ink">{run.triggerSource}</StatusPill>
          ) : (
            <Text type="secondary">—</Text>
          )}
        </MetaCell>
        <MetaCell label="Run at" isDark={isDark}>
          <Text style={{ fontSize: 13 }}>{new Date(run.runDate).toLocaleString()}</Text>
        </MetaCell>
        {run.gitCommitSha && (
          <MetaCell label="Git" isDark={isDark}>
            <Tooltip title={`${run.gitBranch ?? 'branch'} · ${run.gitCommitSha}`}>
              <Text
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 12,
                  color: isDark ? colors.slate[200] : colors.slate[700],
                }}
              >
                <BranchesOutlined style={{ marginRight: 4 }} />
                {run.gitBranch ? `${run.gitBranch} · ` : ''}
                {run.gitCommitSha.slice(0, 8)}
              </Text>
            </Tooltip>
          </MetaCell>
        )}
        {run.safetyOnly && (
          <MetaCell label="Safety only" isDark={isDark}>
            <StatusPill tone="amber" icon={<SafetyOutlined />}>
              Safety scenarios
            </StatusPill>
          </MetaCell>
        )}
      </div>
    </SectionCard>
  );
}

function MetaCell({
  label,
  children,
  isDark,
}: {
  label: string;
  children: React.ReactNode;
  isDark: boolean;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: isDark ? colors.slate[400] : colors.slate[500],
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div>{children}</div>
    </div>
  );
}

// ── Scenarios table ────────────────────────────────────────────────────────

const ScenariosTable = observer(function ScenariosTable({
  results,
  isDark,
  onOpen,
}: {
  results: ScenarioResultSummary[];
  isDark: boolean;
  onOpen: (resultId: number) => void;
}) {
  const columns: ColumnsType<ScenarioResultSummary> = [
    {
      title: '',
      key: 'icon',
      width: 44,
      render: (_t, row) =>
        row.passed ? (
          <CheckCircleFilled style={{ color: colors.green[600], fontSize: 18 }} />
        ) : (
          <CloseCircleFilled style={{ color: colors.red[600], fontSize: 18 }} />
        ),
    },
    {
      title: 'Scenario',
      dataIndex: 'scenarioId',
      key: 'scenarioId',
      sorter: (a, b) => (a.scenarioId ?? '').localeCompare(b.scenarioId ?? ''),
      render: (id: string, row) => (
        <div>
          <Button
            type="link"
            onClick={() => onOpen(row.id)}
            aria-label={`Open the conversation for ${id}`}
            style={{ padding: 0, height: 'auto' }}
          >
          <Text
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 13,
              fontWeight: 600,
              color: row.passed
                ? isDark ? colors.slate[100] : colors.slate[900]
                : colors.red[600],
            }}
          >
            {id}
          </Text>
          </Button>
          {(row.isSafety || row.isCritical) && (
            <div style={{ marginTop: 3, display: 'flex', gap: 4 }}>
              {row.isSafety && (
                <StatusPill tone="amber" icon={<SafetyOutlined />}>
                  Safety
                </StatusPill>
              )}
              {row.isCritical && <StatusPill tone="red">Critical</StatusPill>}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Score',
      dataIndex: 'score',
      key: 'score',
      width: 110,
      align: 'right',
      sorter: (a, b) => Number(a.score) - Number(b.score),
      render: (n: number) => {
        const v = Number(n);
        const tone = v >= 95 ? colors.green[600] : v >= 80 ? colors.amber[600] : colors.red[600];
        return (
          <Text
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 12.5,
              fontWeight: 600,
              color: tone,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {v.toFixed(0)}
          </Text>
        );
      },
    },
    {
      title: 'Checks',
      key: 'checks',
      width: 120,
      align: 'right',
      render: (_t, row) => (
        <Text
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 12,
            color: isDark ? colors.slate[300] : colors.slate[600],
          }}
        >
          {row.passCount}/{row.totalChecks}
        </Text>
      ),
    },
    {
      title: 'Turns',
      dataIndex: 'turnCount',
      key: 'turns',
      width: 80,
      align: 'right',
    },
    {
      title: 'Failed assertions',
      dataIndex: 'failedAssertionCount',
      key: 'failedAssertions',
      width: 130,
      align: 'right',
      render: (n: number) =>
        n > 0 ? (
          <span style={{ color: colors.red[600], fontWeight: 600 }}>{n}</span>
        ) : (
          <Text type="secondary">0</Text>
        ),
    },
    {
      title: 'Elapsed',
      dataIndex: 'elapsedMs',
      key: 'elapsed',
      width: 100,
      align: 'right',
      sorter: (a, b) => a.elapsedMs - b.elapsedMs,
      render: (n: number) => (
        <Text
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 12,
            color: isDark ? colors.slate[300] : colors.slate[600],
          }}
        >
          {formatElapsed(n)}
        </Text>
      ),
    },
    // Phase 1d: cost + token columns (null on legacy rows; rendered as em-dash).
    {
      title: 'Cost',
      dataIndex: 'estimatedCostUsd',
      key: 'cost',
      width: 90,
      align: 'right',
      sorter: (a, b) => (a.estimatedCostUsd ?? 0) - (b.estimatedCostUsd ?? 0),
      render: (n: number | null | undefined) => (
        <Text
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 12,
            color: isDark ? colors.slate[300] : colors.slate[600],
          }}
        >
          {n != null ? `$${n.toFixed(4)}` : '—'}
        </Text>
      ),
    },
    {
      title: 'Tokens',
      key: 'tokens',
      width: 110,
      align: 'right',
      sorter: (a, b) => ((a.inputTokens ?? 0) + (a.outputTokens ?? 0))
                      - ((b.inputTokens ?? 0) + (b.outputTokens ?? 0)),
      render: (_t, row) => {
        const inT = row.inputTokens;
        const outT = row.outputTokens;
        if (inT == null && outT == null) {
          return (
            <Text style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: isDark ? colors.slate[400] : colors.slate[400] }}>
              —
            </Text>
          );
        }
        return (
          <Tooltip title={`in ${inT ?? 0} · out ${outT ?? 0}`}>
            <Text
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 12,
                color: isDark ? colors.slate[300] : colors.slate[600],
              }}
            >
              {(inT ?? 0).toLocaleString()}↓ {(outT ?? 0).toLocaleString()}↑
            </Text>
          </Tooltip>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: 60,
      align: 'right',
      render: (_t, row) =>
        row.error ? (
          <Tooltip title={`Trace error: ${row.error}`}>
            <WarningFilled style={{ color: colors.red[600], fontSize: 14 }} />
          </Tooltip>
        ) : null,
    },
  ];

  return (
    <Table<ScenarioResultSummary>
      rowKey="id"
      columns={columns}
      dataSource={results}
      pagination={results.length > 25 ? { pageSize: 25, showSizeChanger: true } : false}
      size="small"
      locale={{
        emptyText: <BrandedEmpty title="No scenario results" description="This run has no recorded scenarios." size="sm" />,
      }}
      expandable={{
        rowExpandable: (row) => !!row.error,
        expandedRowRender: (row) =>
          row.error ? (
            <Paragraph
              style={{
                margin: '8px 0',
                padding: 12,
                background: isDark ? `${colors.red[700]}33` : colors.red[100],
                border: `1px solid ${colors.red[600]}55`,
                borderRadius: 6,
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 12,
                whiteSpace: 'pre-wrap',
                color: isDark ? colors.red[100] : colors.red[700],
              }}
            >
              {row.error}
            </Paragraph>
          ) : null,
      }}
    />
  );
});

// ── Helpers ────────────────────────────────────────────────────────────────

function formatElapsed(ms: number | undefined | null): string {
  if (!ms) return '—';
  if (ms < 1000) return `${ms} ms`;
  const sec = ms / 1000;
  if (sec < 60) return `${sec.toFixed(1)}s`;
  return `${(sec / 60).toFixed(1)}m`;
}

// Quiet unused-import warnings
void Tag;
void CopyOutlined;
